import React, { useRef, useState } from 'react'
import type { PopperChildrenProps, ReferenceChildrenProps } from 'react-popper'
import { Manager, Popper, Reference } from 'react-popper'

import { Button, Checkbox, Icon, useOutsideClick } from '@fourthwall/components'
import { CalendarIcon, CalendarScheduledIcon } from '@fourthwall/icons'

import { TimezonedCalendar } from '../../../../Timezone/components/TimezonedCalendar'
import * as S from './ScheduleInput.styled'

type Props = {
  date?: Date
  minDate?: Date
  label?: string
  description?: string
  onDateChange: (date?: Date) => void
}

export const ScheduleInput = ({
  date,
  minDate,
  label = 'Set release date',
  description = 'Publish post at specific time.',
  onDateChange,
}: Props) => {
  const [isOpen, setOpen] = useState(false)
  const [isCalendarVisible, setIsCalendarVisible] = useState(!!date)
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setOpen(false)
  })

  const toggleOpen = () => {
    setOpen((prevIsOpen) => !prevIsOpen)
  }

  const renderReference = ({ ref }: ReferenceChildrenProps) => {
    if (date) {
      return (
        <div ref={ref}>
          <Button
            size="small"
            appearance="primary"
            icon={<Icon component={CalendarScheduledIcon} height={19} />}
            onClick={toggleOpen}
          />
        </div>
      )
    }

    return (
      <div ref={ref} data-testid="ScheduleInput.CalendarIcon">
        <Button
          size="small"
          appearance="primary"
          icon={<Icon component={CalendarIcon} height={19} />}
          onClick={toggleOpen}
        />
      </div>
    )
  }

  const renderCalendar = ({ ref, style }: PopperChildrenProps) => (
    <div ref={ref} style={{ ...style, paddingTop: 8, zIndex: 1 }}>
      <S.CheckboxContainer data-testid="ScheduleInput.Checkbox">
        <Checkbox
          value={isCalendarVisible}
          label={label}
          description={description}
          onValueChange={(value) => {
            setIsCalendarVisible(value)

            if (!value) {
              setTimeout(() => {
                onDateChange(undefined)
              }, 0)
            }
          }}
        />
      </S.CheckboxContainer>

      {isCalendarVisible && (
        <TimezonedCalendar
          type="single"
          selectedDates={date ? [date] : []}
          onDatesChange={([date]) => onDateChange(date)}
          timeLabels={['Publish time']}
          minDate={minDate}
        />
      )}
    </div>
  )

  return (
    <Manager>
      <div ref={containerRef}>
        <Reference>{renderReference}</Reference>
        {isOpen && <Popper placement="bottom-end">{renderCalendar}</Popper>}
      </div>
    </Manager>
  )
}
